/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * JUMBF (ISO/IEC 19566-5) superbox parser, as used by C2PA manifest stores.
 *
 * A JUMBF superbox (`jumb`) starts with a Description box (`jumd`) and is
 * followed by content boxes (nested `jumb` superboxes, or leaf data boxes such
 * as `cbor`, `json`, `bidb`/`bfdb`, `uuid`). The `jumd` type is a 16-byte UUID;
 * when it encodes a 4-character code it has the standard JUMBF suffix
 * `00 11 00 10 80 00 00 AA 00 38 9B 71`, and the leading 4 bytes are the role
 * (`c2pa`, `c2ma`, `c2cl`, `c2as`, `c2cs`, `cbor`, `json`, …).
 */

import { fourCC } from '../bmff/parseBoxes'

/** Standard JUMBF UUID suffix for a type expressed as a 4-character code. */
const JUMBF_4CC_SUFFIX = [0x00, 0x11, 0x00, 0x10, 0x80, 0x00, 0x00, 0xaa, 0x00, 0x38, 0x9b, 0x71]

/** Max JUMBF superbox nesting — bounds adversarial nested `jumb` boxes (stack safety). */
const MAX_JUMBF_DEPTH = 256

const utf8 = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null
/** Decodes a byte span without spreading it into arguments (safe for large/unterminated spans). */
function decodeAscii (u8: Uint8Array): string {
    if (utf8) return utf8.decode(u8)
    let s = ''
    for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i])
    return s
}

/** Full type UUID of a JUMBF Embedded File superbox (`40CB0C32-…`). */
const EMBEDDED_FILE_UUID = [0x40, 0xcb, 0x0c, 0x32, 0xbb, 0x8a, 0x48, 0x9d, 0xa7, 0x0b, 0x2a, 0xd6, 0xf4, 0x7f, 0x43, 0x69]

/** A parsed JUMBF box (superbox or leaf data box). */
export interface JumbfBox {
    /** 4-character role from the `jumd` type when it is a 4CC-suffixed UUID (e.g. `c2pa`, `cbor`); else `''`. */
    type: string
    /** Full 16-byte `jumd` type UUID. */
    typeUuid: Uint8Array
    /** Human-readable label from the `jumd` box, when present. */
    label?: string
    /** Child boxes of a superbox (empty for leaf data boxes). */
    children: JumbfBox[]
    /** Leaf data-box 4CC, e.g. `cbor`, `json`, `bidb` (only for leaves). */
    dataType?: string
    /** Leaf payload bytes (for `cbor`/`json`/`bidb`/`uuid` data boxes). */
    data?: Uint8Array
    /** Media type from a `bfdb` (binary file description) sibling, when an embedded file. */
    mediaType?: string
    /** Absolute payload byte range of this box's `jumb` superbox in the source buffer. */
    range: { start: number, end: number }
}

function hasSuffix4cc (uuid: Uint8Array): boolean {
    for (let i = 0; i < 12; i++) {
        if (uuid[i + 4] !== JUMBF_4CC_SUFFIX[i]) return false
    }
    return true
}

function uuidEquals (uuid: Uint8Array, expected: number[]): boolean {
    if (uuid.length !== 16) return false
    for (let i = 0; i < 16; i++) {
        if (uuid[i] !== expected[i]) return false
    }
    return true
}

/**
 * Parses one JUMBF `jumb` superbox starting at `start` within `u8`.
 * Returns the box and the offset one past its end, or `null` if it isn't a `jumb` box.
 */
function parseSuperbox (u8: Uint8Array, start: number, end: number, depth = 0): { box: JumbfBox, next: number } | null {
    if (depth > MAX_JUMBF_DEPTH) return null
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength)
    if (start + 8 > end) return null
    const size = dv.getUint32(start, false)
    const type = fourCC(u8, start + 4)
    if (type !== 'jumb') return null
    const boxEnd = size === 0 ? end : start + size
    if (boxEnd > end || size < 8) return null

    // First child must be the description box `jumd`.
    let off = start + 8
    const jumdSize = dv.getUint32(off, false)
    if (fourCC(u8, off + 4) !== 'jumd') return null
    const jumdEnd = off + jumdSize
    // Bound the description box against the enclosing superbox before trusting its
    // size (PARSE-2/PARSE-4). `jumdSize` is attacker-controlled; without this guard
    // an oversized value lets `jumdEnd` run ~4 GB past the buffer, and the label
    // scan below (`while (p < jumdEnd …)`) reads `undefined !== 0` forever — a
    // non-throwing CPU hang that escapes the top-level catch. A jumd box needs at
    // least LBox+TBox(8)+UUID(16) = 24 bytes and must fit inside the superbox.
    if (jumdSize < 24 || jumdEnd > boxEnd) return null

    const typeUuid = u8.subarray(off + 8, off + 24)
    let p = off + 24
    const toggles = u8[p]; p += 1
    let label: string | undefined
    if (toggles & 0x02) {
        const lstart = p
        while (p < jumdEnd && u8[p] !== 0) p++
        label = decodeAscii(u8.subarray(lstart, p))
        p += 1 // skip NUL
    }
    // toggles & 0x04 → 4-byte ID; toggles & 0x08 → 32-byte signature. We don't need either.

    const box: JumbfBox = {
        type: hasSuffix4cc(typeUuid) ? fourCC(typeUuid, 0) : '',
        typeUuid: new Uint8Array(typeUuid),
        label,
        children: [],
        range: { start: start + 8, end: boxEnd }
    }

    // Remaining content boxes after the description box.
    off = jumdEnd
    while (off + 8 <= boxEnd) {
        const childSize = dv.getUint32(off, false)
        const childType = fourCC(u8, off + 4)
        const childEnd = childSize === 0 ? boxEnd : off + childSize
        if (childEnd > boxEnd || childSize < 8) break

        if (childType === 'jumb') {
            const parsed = parseSuperbox(u8, off, boxEnd, depth + 1)
            if (!parsed) break
            box.children.push(parsed.box)
        } else {
            // Leaf data box (cbor / json / bidb / bfdb / uuid …).
            const payload = u8.subarray(off + 8, childEnd)
            if (childType === 'bfdb') {
                // binary file description: 1 toggle byte then null-terminated media type.
                let q = off + 8 + 1
                const mstart = q
                while (q < childEnd && u8[q] !== 0) q++
                box.mediaType = decodeAscii(u8.subarray(mstart, q))
            } else {
                const leaf: JumbfBox = {
                    type: '',
                    typeUuid: new Uint8Array(0),
                    children: [],
                    dataType: childType,
                    data: payload,
                    range: { start: off + 8, end: childEnd }
                }
                box.children.push(leaf)
            }
        }
        off = childEnd
    }

    // Lift a `bfdb` media type onto the box (embedded-file superbox convenience).
    if (uuidEquals(box.typeUuid, EMBEDDED_FILE_UUID) && box.mediaType === undefined) {
        box.mediaType = ''
    }
    return { box, next: boxEnd }
}

/** Parses the top-level JUMBF superbox from `u8` (the C2PA manifest store). */
export function parseJumbf (u8: Uint8Array): JumbfBox | null {
    const parsed = parseSuperbox(u8, 0, u8.byteLength)
    return parsed ? parsed.box : null
}

/** Returns the first child whose 4CC role equals `type`. */
export function childByType (box: JumbfBox, type: string): JumbfBox | undefined {
    return box.children.find(c => c.type === type)
}

/** Returns the first leaf child carrying a `dataType` data box (e.g. `cbor`, `json`). */
export function dataChild (box: JumbfBox, dataType: string): JumbfBox | undefined {
    return box.children.find(c => c.dataType === dataType)
}
